import { cn } from "@/lib/utils.ts";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const Tabs = TabsPrimitive.Root;

const listVariants = cva("inline-flex items-stretch justify-center text-muted-foreground", {
  variants: {
    variant: {
      default: "rounded-full gap-2",
      popover: "rounded-full gap-2",
      underline: "gap-6 border-b border-border",
      sidebar:
        "flex flex-col items-stretch justify-start gap-0.5 bg-transparent p-0 w-full text-foreground",
    },
    size: {
      default: "h-10",
      sm: "h-8",
      sidebar: "h-auto",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

const triggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "rounded-full data-[state=active]:bg-tertiary data-[state=active]:text-tertiary-foreground",
        popover:
          "rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground",
        underline:
          "relative border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground pb-3.5",
        sidebar:
          "justify-start w-full rounded-md hover:bg-accent/50 data-[state=active]:bg-accent/50",
      },
      size: {
        default: "text-sm px-3 py-1.5",
        sm: "text-xs px-3 py-1 h-auto",
        underline: "text-sm px-0 py-0",
        sidebar: "text-sm gap-2 px-3 py-2 h-auto",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & VariantProps<typeof listVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TabsPrimitive.List
    className={cn(listVariants({ variant, size }), className)}
    ref={ref}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof triggerVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TabsPrimitive.Trigger
    className={cn(triggerVariants({ variant, size }), className)}
    ref={ref}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    ref={ref}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
